"use client";

import { useState } from "react";
import { cn } from "@/lib/tailwind-utils";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { 
  FaMoneyBillWave, 
  FaChevronRight, 
  FaInfoCircle, 
  FaSearch, 
  FaTags
} from "react-icons/fa";

import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

export default function TarifLayananClient({ tarif }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Semua");

  const categories = ["Semua", ...new Set(tarif?.map(item => item.kategori) || [])];

  const filteredTarifs = tarif?.filter(item => {
    const matchesSearch = item.nama_layanan.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.kategori.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "Semua" || item.kategori === selectedCategory;
    return matchesSearch && matchesCategory;
  }) || [];

  const groupedTarifs = filteredTarifs.reduce((acc, current) => {
    if (!acc[current.kategori]) acc[current.kategori] = [];
    acc[current.kategori].push(current);
    return acc;
  }, {});

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!tarif || tarif.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
        <p className="text-slate-400 font-medium">Data tarif belum tersedia.</p>
      </div>
    );
  }

  // Pre-open the first category if it exists during grouping
  const groupKeys = Object.keys(groupedTarifs);
  const defaultAccordionValue = groupKeys.length > 0 ? [groupKeys[0]] : [];

  return (
    <div className="w-full space-y-12">
      {/* Search & Filter Bar */}
      <div className="bg-white p-4 md:p-6 rounded-[32px] shadow-xl border border-blue-50/50 flex flex-col md:flex-row gap-6 items-center">
        {/* Search Input */}
        <div className="relative flex-1 w-full">
          <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Cari nama layanan atau kategori..."
            className="w-full pl-12 pr-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-blue-500/20 text-slate-700 font-medium placeholder:text-slate-400 transition-all outline-none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 shrink-0">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={cn(
                "px-5 py-2.5 rounded-xl text-[13px] font-black transition-all duration-300 border-2 uppercase tracking-wide",
                selectedCategory === cat 
                  ? "bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/30" 
                  : "bg-white border-slate-100 text-slate-500 hover:border-blue-200 hover:text-blue-600"
              )}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped Content with Accordion */}
      {groupKeys.length > 0 ? (
        <Accordion type="multiple" defaultValue={defaultAccordionValue} className="space-y-6">
          {groupKeys.map((category, idx) => (
            <AccordionItem 
              key={category} 
              value={category} 
              className="border-none bg-white rounded-[40px] shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              <AccordionTrigger className="px-8 py-6 hover:no-underline group data-[state=open]:bg-blue-50/40 transition-colors">
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform">
                    <FaTags className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">{category}</h2>
                    <p className="text-[12px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                      {groupedTarifs[category].length} Jenis Layanan
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              
              <AccordionContent className="px-8 pb-10 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-top-4 duration-500">
                  {groupedTarifs[category].map((item) => (
                    <Card key={item.id} className="group p-8 rounded-[40px] border border-slate-50 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-500 bg-white overflow-hidden relative">
                      <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-125 transition-transform duration-700">
                        <FaMoneyBillWave className="w-20 h-20 text-blue-900" />
                      </div>
                      
                      <div className="relative z-10 flex flex-col h-full space-y-6">
                        <div className="flex-1 space-y-3">
                          <h3 className="text-[17px] font-black text-slate-800 leading-tight group-hover:text-blue-600 transition-colors">
                            {item.nama_layanan}
                          </h3>
                          {item.keterangan && (
                            <div className="flex gap-2 items-start text-slate-400 text-[12px] italic font-medium">
                              <FaInfoCircle className="mt-0.5 shrink-0" />
                              <p className="line-clamp-3">{item.keterangan}</p>
                            </div>
                          )}
                        </div>

                        <div className="pt-6 border-t border-slate-50 flex items-center justify-between">
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Besaran Tarif</p>
                            <span className="text-xl font-black text-blue-600">{formatCurrency(item.harga)}</span>
                            <span className="text-slate-400 text-[12px] font-bold ml-1">/ {item.satuan}</span>
                          </div>
                          
                          <div className={cn(
                            "w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500"
                          )}>
                            <FaChevronRight className="w-3.5 h-3.5 translate-x-[-1px] group-hover:translate-x-0 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="text-center py-24 bg-white rounded-[40px] border-2 border-dashed border-slate-100 shadow-sm">
           <div className="max-w-md mx-auto space-y-6">
              <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-300">
                <FaSearch className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-800 mb-2">Pencarian Tidak Ditemukan</h3>
                <p className="text-slate-500 font-medium text-sm">Maaf, layanan yang Anda cari tidak tersedia dalam kategori ini.</p>
              </div>
              <button 
                onClick={() => { setSearchQuery(""); setSelectedCategory("Semua"); }}
                className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-500/20 hover:scale-105 transition-transform text-sm"
              >
                Reset Filter
              </button>
           </div>
        </div>
      )}
    </div>
  );
}
